import { createFileRoute, useParams, useNavigate, useSearch, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../../components/ui/alert-dialog'
import { Label } from '../../../components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select'
import { mdiPlus, mdiRefresh, mdiFolder, mdiCheck, mdiClose, mdiDelete, mdiUpload } from '@mdi/js'
import { AppIcon } from '../../../components/ui/icon'
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs'
import { useIsMobile } from '../../../hooks/use-mobile'
import { FolderLayoutImportDialog } from '../../../components/folder-layouts/FolderLayoutImportDialog'
import { apiClient } from '../../../api/client'
import { formatCreated } from '../../../lib/format'
import type { components } from '../../../api/schema'
import { Can, useCan } from '../../../contexts/PermissionsContext'
import { PageHeader } from '../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../lib/section-icons'
import { EmptyState } from '../../../components/ui/empty-state'
import { mdiFolderMultipleOutline } from '@mdi/js'

export const Route = createFileRoute('/$org/folder-layouts/')({
    component: DocumentLayoutsPage,
    // Filtering is URL-driven so a filtered list is bookmarkable and shareable
    // (front/CLAUDE.md — "all UI changes should result in routing changes,
    // especially filtering operations").
    validateSearch: (search: Record<string, unknown>): { target?: string } => ({
        target: typeof search.target === 'string' ? search.target : undefined,
    }),
})

type DocumentFolderLayout = components['schemas']['DocumentFolderLayout']
type CreateDocumentFolderLayoutRequest = components['schemas']['CreateDocumentFolderLayoutRequest']
type UpdateDocumentFolderLayoutRequest = components['schemas']['UpdateDocumentFolderLayoutRequest']
type LayoutTarget = NonNullable<DocumentFolderLayout['target_entity_type']>

/**
 * Which entity a layout structures the documents of (spec 2026-08-13-01 §2).
 * Chosen at creation and immutable afterwards, so the list shows it as a badge
 * rather than an editable cell.
 */
const LAYOUT_TARGETS: LayoutTarget[] = ['asset', 'contract', 'user', 'issue']

/** Sentinel for "no target filter" — Radix forbids an empty-string item. */
const ALL_TARGETS = '__all__'

/**
 * Read-only badge naming a layout's target entity type. The target is set at
 * creation and immutable (changing it would silently detach every asset or
 * contract already resolving the layout), so it is never an editable cell.
 */
function LayoutTargetBadge({ target, label, uid }: { target?: LayoutTarget; label: string; uid: string }) {
    const tone = target === 'contract'
        ? 'border-primary/40 bg-primary/10 text-primary'
        : 'border-border bg-muted text-muted-foreground'
    return (
        <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}
            data-testid={`layout-target-${uid}`}
            data-target={target || 'asset'}
        >
            {label}
        </span>
    )
}

function DocumentLayoutsPage() {
    const { org } = useParams({ from: '/$org/folder-layouts/' })
    const { t } = useTranslation('folders')
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
    const [newLayout, setNewLayout] = useState<{ name: { en: string; fr: string }; target_entity_type: LayoutTarget }>({
        name: { en: '', fr: '' },
        target_entity_type: 'asset',
    })
    const navigate = useNavigate()
    const routeSearch = useSearch({ from: '/$org/folder-layouts/' })
    const targetFilter = routeSearch.target && LAYOUT_TARGETS.includes(routeSearch.target as LayoutTarget)
        ? routeSearch.target
        : ALL_TARGETS
    const setTargetFilter = (value: string) => navigate({
        to: '/$org/folder-layouts',
        params: { org },
        search: { target: value === ALL_TARGETS ? undefined : value },
    })
    const [editingId, setEditingId] = useState<string | null>(null)
    const [editForm, setEditForm] = useState<{ name: { en: string; fr: string } }>({ name: { en: '', fr: '' } })
    const [pendingDeleteUid, setPendingDeleteUid] = useState<string | null>(null)
    const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
    // Non-blocking notification (never a toast, per front/CLAUDE.md).
    const [importNotice, setImportNotice] = useState<string | null>(null)
    const isMobile = useIsMobile()

    const queryClient = useQueryClient()

    // Folder layouts are org configuration: creating, renaming, importing and
    // deleting one all require `configuration.write`. Readers keep the list and
    // every name on screen — only the write affordances go away
    // (spec 2026-09-04-02 §2 "Gating").
    const canWrite = useCan('configuration.write')

    // Set up breadcrumbs
    useBreadcrumbs([
        { title: t('common:nav.home'), path: `/${org}` },
        { title: t('common:nav.settings'), path: `/${org}/settings` },
        { title: t('breadcrumb') }
    ])

    // Fetch document layouts
    const {
        data: layouts = [],
        isLoading,
        error,
        refetch
    } = useQuery({
        queryKey: ['documentLayouts', org, targetFilter],
        queryFn: async (): Promise<DocumentFolderLayout[]> => {
            const response = await apiClient.GET('/api/v1/organizations/{orgCode}/document-folders-layouts', {
                params: {
                    path: { orgCode: org },
                    // Server-side filter, so the URL-less UI still asks the API
                    // exactly what it renders (spec 2026-08-13-01 §6).
                    query: targetFilter === ALL_TARGETS ? undefined : { target_entity_type: targetFilter as LayoutTarget },
                }
            })

            if (response.error) {
                throw new Error(JSON.stringify(response.error) || 'Failed to load document layouts')
            }

            return (response.data as { layouts?: DocumentFolderLayout[] })?.layouts || []
        },
    })

    // Create layout mutation
    const createLayoutMutation = useMutation({
        mutationFn: async (data: CreateDocumentFolderLayoutRequest) => {
            const response = await apiClient.POST('/api/v1/organizations/{orgCode}/document-folders-layouts', {
                params: { path: { orgCode: org } },
                body: data
            })

            if (response.error) {
                throw new Error(JSON.stringify(response.error) || 'Failed to create layout')
            }

            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['documentLayouts', org] })
            setIsCreateDialogOpen(false)
            setNewLayout({ name: { en: '', fr: '' }, target_entity_type: 'asset' })
        }
    })

    // Update layout mutation
    const updateLayoutMutation = useMutation({
        mutationFn: async ({ uid, data }: { uid: string; data: UpdateDocumentFolderLayoutRequest }) => {
            const response = await apiClient.PATCH('/api/v1/organizations/{orgCode}/document-folders-layouts/{layoutUid}', {
                params: {
                    path: { orgCode: org, layoutUid: uid }
                },
                body: data
            })

            if (response.error) {
                throw new Error(JSON.stringify(response.error) || 'Failed to update layout')
            }

            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['documentLayouts', org] })
            setEditingId(null)
        }
    })

    // Delete layout mutation
    const deleteLayoutMutation = useMutation({
        mutationFn: async (uid: string) => {
            const response = await apiClient.DELETE('/api/v1/organizations/{orgCode}/document-folders-layouts/{layoutUid}', {
                params: {
                    path: { orgCode: org, layoutUid: uid }
                }
            })

            if (response.error) {
                throw new Error(JSON.stringify(response.error) || 'Failed to delete layout')
            }

            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['documentLayouts', org] })
        }
    })

    const startEditing = (layout: DocumentFolderLayout) => {
        // A read-only caller must not be able to enter edit mode at all.
        if (!canWrite) return
        setEditingId(layout.uid)
        setEditForm({
            name: { en: layout.name.en || '', fr: layout.name.fr || '' }
        })
    }

    const cancelEditing = () => {
        setEditingId(null)
        setEditForm({ name: { en: '', fr: '' } })
    }

    const saveEdit = () => {
        if (!editingId) return
        updateLayoutMutation.mutate({ uid: editingId, data: { name: editForm.name } })
    }

    const handleDelete = (uid: string) => {
        setPendingDeleteUid(uid)
    }

    const confirmDelete = () => {
        if (!pendingDeleteUid) return
        deleteLayoutMutation.mutate(pendingDeleteUid)
        setPendingDeleteUid(null)
    }

    const handleRefresh = () => {
        refetch()
    }

    const renderLocalizedNames = (names: { [key: string]: string }) => {
        const entries = Object.entries(names).filter(([, value]) => value && value.trim())
        if (entries.length === 0) return <span className="text-muted-foreground">—</span>
        return (
            <div className="flex flex-col gap-1">
                {entries.map(([lang, name]) => (
                    <div key={lang} className="flex items-center gap-2">
                        <span className="text-sm">{lang === 'en' ? '🇺🇸' : lang === 'fr' ? '🇫🇷' : '🏳️'}</span>
                        <span className="text-sm font-medium">{name}</span>
                    </div>
                ))}
            </div>
        )
    }

    if (error) {
        return (
            <div>
                <div className="text-center">
                    <p className="text-destructive">{t('error_loading', { message: (error as any)?.title || (error as any)?.message || 'Unknown error' })}</p>
                    <Button onClick={handleRefresh} className="mt-4">
                        <AppIcon path={mdiRefresh} size="md" className="mr-2" />
                        {t('retry_button')}
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className="max-w-7xl mx-auto">
            {/* Header */}
            <PageHeader
                className="mb-6"
                icon={SECTION_ICONS.folderLayouts}
                title={t('page_title')}
                description={t('page_description')}
                actions={
                <>
                    <Select value={targetFilter} onValueChange={setTargetFilter}>
                        <SelectTrigger className="w-40" data-testid="layouts-target-filter" aria-label={t('filter_target_label')}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL_TARGETS} data-testid="layouts-target-filter-all">
                                {t('filter_target_all')}
                            </SelectItem>
                            {LAYOUT_TARGETS.map(target => (
                                <SelectItem key={target} value={target} data-testid={`layouts-target-filter-${target}`}>
                                    {t(`target.${target}` as never)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button
                        onClick={handleRefresh}
                        variant="outline"
                        data-testid="refresh-layouts"
                        aria-label={t('refresh_button')}
                        title={t('refresh_button')}
                    >
                        <AppIcon path={mdiRefresh} size="md" className="md:mr-2" />
                        <span className="hidden md:inline">{t('refresh_button')}</span>
                    </Button>
                    <Can perm="configuration.write">
                        <Button
                            onClick={() => { setImportNotice(null); setIsImportDialogOpen(true) }}
                            variant="outline"
                            data-testid="import-layout-button"
                            aria-label={t('import.button')}
                            title={t('import.button')}
                        >
                            <AppIcon path={mdiUpload} size="md" className="md:mr-2" />
                            <span className="hidden md:inline">{t('import.button')}</span>
                        </Button>
                    </Can>
                    <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                        <Can perm="configuration.write">
                          <DialogTrigger asChild>
                              <Button
                                  data-testid="create-layout-button"
                                  aria-label={t('create_layout_button')}
                                  title={t('create_layout_button')}
                              >
                                  <AppIcon path={mdiPlus} size="md" className="md:mr-2" />
                                  <span className="hidden md:inline">{t('create_layout_button')}</span>
                              </Button>
                          </DialogTrigger>
                        </Can>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>{t('create_dialog.title')}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 p-6">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="name-en" className="flex items-center gap-2">
                                            <span>🇺🇸</span>
                                            {t('create_dialog.name_en_label')}
                                        </Label>
                                        <Input
                                            id="name-en"
                                            placeholder="e.g., Property Documents"
                                            value={newLayout.name.en}
                                            onChange={(e) => setNewLayout(prev => ({ ...prev, name: { ...prev.name, en: e.target.value } }))}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="name-fr" className="flex items-center gap-2">
                                            <span>🇫🇷</span>
                                            {t('create_dialog.name_fr_label')}
                                        </Label>
                                        <Input
                                            id="name-fr"
                                            placeholder="e.g., Documents de Propriété"
                                            value={newLayout.name.fr}
                                            onChange={(e) => setNewLayout(prev => ({ ...prev, name: { ...prev.name, fr: e.target.value } }))}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <Label htmlFor="layout-target">{t('create_dialog.target_label')}</Label>
                                    <Select
                                        value={newLayout.target_entity_type}
                                        onValueChange={(value) => setNewLayout(prev => ({ ...prev, target_entity_type: value as LayoutTarget }))}
                                    >
                                        <SelectTrigger id="layout-target" data-testid="create-layout-target">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {LAYOUT_TARGETS.map(target => (
                                                <SelectItem key={target} value={target} data-testid={`create-layout-target-${target}`}>
                                                    {t(`target.${target}` as never)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="mt-1 text-xs text-muted-foreground">{t('create_dialog.target_hint')}</p>
                                </div>

                                <div className="flex justify-end gap-2 pt-4">
                                    <Button
                                        variant="outline"
                                        onClick={() => setIsCreateDialogOpen(false)}
                                        disabled={createLayoutMutation.isPending}
                                    >
                                        {t('create_dialog.cancel')}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            const data: CreateDocumentFolderLayoutRequest = {
                                                name: newLayout.name,
                                                target_entity_type: newLayout.target_entity_type,
                                            }
                                            createLayoutMutation.mutate(data)
                                        }}
                                        disabled={createLayoutMutation.isPending || (!newLayout.name.en && !newLayout.name.fr)}
                                        data-testid="create-layout-submit"
                                    >
                                        {createLayoutMutation.isPending ? t('create_dialog.creating') : t('create_dialog.submit')}
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>
                </>
                }
            />

            {importNotice && (
                <div
                    className="mb-4 rounded-lg border border-primary/40 bg-primary/10 px-4 py-2 text-sm"
                    role="status"
                    data-testid="import-layout-notice"
                >
                    {importNotice}
                </div>
            )}

            {/* Count header */}
            <div className="mb-4">
                <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                    <AppIcon path={mdiFolder} size="md" />
                    {t('layout_count', { count: layouts.length })}
                </h2>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <div className="text-muted-foreground">{t('loading')}</div>
                </div>
            ) : isMobile ? (
                /* Mobile: a stacked card list — the 5-column table is unusable
                   below `md` (see spec 2026-08-01-08). Rendered conditionally so
                   every data-testid resolves to exactly one element. */
                <div className="space-y-3" data-testid="folder-layouts-list-mobile">
                    {layouts.length === 0 ? (
                        <EmptyState
                            tone="solid"
                            icon={mdiFolderMultipleOutline}
                            title={t('no_layouts.description_empty')}
                            action={
                                <Can perm="configuration.write">
                                    <Button onClick={() => setIsCreateDialogOpen(true)} data-testid="empty-state-create-button">
                                        <AppIcon path={mdiPlus} size="md" className="mr-2" />
                                        {t('no_layouts.create_button')}
                                    </Button>
                                </Can>
                            }
                        />
                    ) : layouts.map(layout => {
                        const isEditing = canWrite && editingId === layout.uid
                        return (
                            <div
                                key={layout.uid}
                                className="bg-card shadow rounded-md border border-border p-4 space-y-3"
                                data-testid={`folder-layout-row-${layout.uid}`}
                            >
                                {isEditing ? (
                                    <div
                                        className="grid grid-cols-1 gap-2"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault()
                                                saveEdit()
                                            } else if (e.key === 'Escape') {
                                                cancelEditing()
                                            }
                                        }}
                                    >
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sm">🇺🇸</span>
                                            <Input
                                                placeholder="Name (EN)"
                                                value={editForm.name.en}
                                                onChange={(e) => setEditForm(prev => ({ ...prev, name: { ...prev.name, en: e.target.value } }))}
                                                className="pl-10"
                                                data-testid={`edit-layout-name-en-${layout.uid}`}
                                                autoFocus
                                            />
                                        </div>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sm">🇫🇷</span>
                                            <Input
                                                placeholder="Name (FR)"
                                                value={editForm.name.fr}
                                                onChange={(e) => setEditForm(prev => ({ ...prev, name: { ...prev.name, fr: e.target.value } }))}
                                                className="pl-10"
                                                data-testid={`edit-layout-name-fr-${layout.uid}`}
                                            />
                                        </div>
                                    </div>
                                ) : canWrite ? (
                                    <button
                                        type="button"
                                        className="text-left cursor-pointer hover:bg-muted/40 rounded px-1 -mx-1 w-full break-words"
                                        onClick={() => startEditing(layout)}
                                        data-testid={`layout-name-${layout.uid}`}
                                        title={t('common:actions.edit')}
                                    >
                                        {renderLocalizedNames(layout.name)}
                                    </button>
                                ) : (
                                    /* Read-only: the name stays on screen, it just stops
                                       being a click-to-edit affordance. */
                                    <div
                                        className="text-left px-1 -mx-1 w-full break-words"
                                        data-testid={`layout-name-${layout.uid}`}
                                    >
                                        {renderLocalizedNames(layout.name)}
                                    </div>
                                )}

                                <div className="flex flex-wrap items-center gap-2">
                                    {(layout as any).code && (
                                        <code className="text-xs bg-muted px-2 py-1 rounded break-all">{(layout as any).code}</code>
                                    )}
                                    <LayoutTargetBadge target={layout.target_entity_type} label={t(`target.${layout.target_entity_type || 'asset'}` as never)} uid={layout.uid as string} />
                                </div>

                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                    <span>
                                        {t('column_created')}:{' '}
                                        <span data-testid={`layout-created-${layout.uid}`}>{formatCreated(layout.created_at)}</span>
                                    </span>
                                    <span>
                                        {t('column_updated')}:{' '}
                                        <span data-testid={`layout-updated-${layout.uid}`}>{formatCreated((layout as any).updated_at)}</span>
                                    </span>
                                </div>

                                <div className="flex items-center gap-2">
                                    {isEditing ? (
                                        <>
                                            <Button
                                                size="sm"
                                                onClick={saveEdit}
                                                disabled={updateLayoutMutation.isPending}
                                                className="flex-1"
                                                data-testid={`save-layout-${layout.uid}`}
                                                aria-label={t('inline_save')}
                                                title={t('inline_save')}
                                            >
                                                <AppIcon path={mdiCheck} size="md" className="mr-2" />
                                                {t('inline_save')}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={cancelEditing}
                                                disabled={updateLayoutMutation.isPending}
                                                className="h-9 w-9 p-0 shrink-0"
                                                data-testid={`cancel-layout-${layout.uid}`}
                                                aria-label={t('inline_cancel')}
                                                title={t('inline_cancel')}
                                            >
                                                <AppIcon path={mdiClose} size="md" />
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Link
                                                to="/$org/folder-layouts/$layoutUid"
                                                params={{ org, layoutUid: layout.uid }}
                                                className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                                                data-testid={`manage-folders-${layout.uid}`}
                                            >
                                                <AppIcon path={mdiFolder} size="xs" />
                                                {t('manage_folders_button')}
                                            </Link>
                                            <Can perm="configuration.write">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => handleDelete(layout.uid)}
                                                    disabled={deleteLayoutMutation.isPending}
                                                    className="h-9 w-9 p-0 shrink-0 text-destructive hover:text-destructive"
                                                    data-testid={`delete-layout-${layout.uid}`}
                                                    aria-label={t('common:actions.delete')}
                                                    title={t('common:actions.delete')}
                                                >
                                                    <AppIcon path={mdiDelete} size="sm" />
                                                </Button>
                                            </Can>
                                        </>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            ) : (
                <div className="bg-card shadow rounded-md border border-border overflow-x-auto" data-testid="folder-layouts-table-wrapper">
                    <table className="min-w-full divide-y divide-border" data-testid="folder-layouts-table">
                        <thead className="bg-muted/50">
                            <tr>
                                <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                    {t('column_name')}
                                </th>
                                <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                    {t('column_code')}
                                </th>
                                <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                    {t('column_target')}
                                </th>
                                <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                    {t('column_created')}
                                </th>
                                <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                    {t('column_updated')}
                                </th>
                                <th className="sticky right-0 z-10 bg-muted/50 px-6 py-3 text-right text-[11px] font-medium text-muted-foreground uppercase tracking-wider shadow-[inset_1px_0_0_0_var(--border)]">
                                    {t('column_actions')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-card divide-y divide-border">
                            {layouts.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6">
                                        <EmptyState
                                            variant="inline"
                                            tone="solid"
                                            className="py-12"
                                            icon={mdiFolderMultipleOutline}
                                            title={t('no_layouts.description_empty')}
                                            action={
                                                <Can perm="configuration.write">
                                                    <Button onClick={() => setIsCreateDialogOpen(true)} data-testid="empty-state-create-button">
                                                        <AppIcon path={mdiPlus} size="md" className="mr-2" />
                                                        {t('no_layouts.create_button')}
                                                    </Button>
                                                </Can>
                                            }
                                        />
                                    </td>
                                </tr>
                            ) : layouts.map(layout => {
                                const isEditing = canWrite && editingId === layout.uid
                                return (
                                    <tr key={layout.uid} className="hover:bg-muted/50" data-testid={`folder-layout-row-${layout.uid}`}>
                                        <td className="px-6 py-4 text-sm text-foreground">
                                            {isEditing ? (
                                                <div
                                                    className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-md"
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault()
                                                            saveEdit()
                                                        } else if (e.key === 'Escape') {
                                                            cancelEditing()
                                                        }
                                                    }}
                                                >
                                                    <div className="relative">
                                                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sm">🇺🇸</span>
                                                        <Input
                                                            placeholder="Name (EN)"
                                                            value={editForm.name.en}
                                                            onChange={(e) => setEditForm(prev => ({ ...prev, name: { ...prev.name, en: e.target.value } }))}
                                                            className="pl-10"
                                                            data-testid={`edit-layout-name-en-${layout.uid}`}
                                                            autoFocus
                                                        />
                                                    </div>
                                                    <div className="relative">
                                                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sm">🇫🇷</span>
                                                        <Input
                                                            placeholder="Name (FR)"
                                                            value={editForm.name.fr}
                                                            onChange={(e) => setEditForm(prev => ({ ...prev, name: { ...prev.name, fr: e.target.value } }))}
                                                            className="pl-10"
                                                            data-testid={`edit-layout-name-fr-${layout.uid}`}
                                                        />
                                                    </div>
                                                </div>
                                            ) : canWrite ? (
                                                <button
                                                    type="button"
                                                    className="text-left cursor-pointer hover:bg-muted/40 rounded px-1 -mx-1 w-full"
                                                    onClick={() => startEditing(layout)}
                                                    data-testid={`layout-name-${layout.uid}`}
                                                    title={t('common:actions.edit')}
                                                >
                                                    {renderLocalizedNames(layout.name)}
                                                </button>
                                            ) : (
                                                /* Read-only: keep the name visible, drop the editor. */
                                                <div
                                                    className="text-left px-1 -mx-1 w-full"
                                                    data-testid={`layout-name-${layout.uid}`}
                                                >
                                                    {renderLocalizedNames(layout.name)}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                            {(layout as any).code ? (
                                                <code className="text-xs bg-muted px-2 py-1 rounded">{(layout as any).code}</code>
                                            ) : (
                                                <span className="text-muted-foreground">—</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <LayoutTargetBadge target={layout.target_entity_type} label={t(`target.${layout.target_entity_type || 'asset'}` as never)} uid={layout.uid as string} />
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground" data-testid={`layout-created-${layout.uid}`}>
                                            {formatCreated(layout.created_at)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground" data-testid={`layout-updated-${layout.uid}`}>
                                            {formatCreated((layout as any).updated_at)}
                                        </td>
                                        <td className="sticky right-0 z-10 bg-card px-6 py-4 whitespace-nowrap text-right text-sm font-medium shadow-[inset_1px_0_0_0_var(--border)]">
                                            <div className="flex items-center justify-end gap-1">
                                                {isEditing ? (
                                                    <>
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={saveEdit}
                                                            disabled={updateLayoutMutation.isPending}
                                                            className="h-9 w-9 p-0"
                                                            data-testid={`save-layout-${layout.uid}`}
                                                            aria-label={t('inline_save')}
                                                        >
                                                            <AppIcon path={mdiCheck} size="md" />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={cancelEditing}
                                                            disabled={updateLayoutMutation.isPending}
                                                            className="h-9 w-9 p-0"
                                                            data-testid={`cancel-layout-${layout.uid}`}
                                                            aria-label={t('inline_cancel')}
                                                        >
                                                            <AppIcon path={mdiClose} size="md" />
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Link
                                                            to="/$org/folder-layouts/$layoutUid"
                                                            params={{ org, layoutUid: layout.uid }}
                                                            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                                                            data-testid={`manage-folders-${layout.uid}`}
                                                        >
                                                            <AppIcon path={mdiFolder} size="xs" />
                                                            {t('manage_folders_button')}
                                                        </Link>
                                                        <Can perm="configuration.write">
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => handleDelete(layout.uid)}
                                                                disabled={deleteLayoutMutation.isPending}
                                                                className="h-9 w-9 p-0 text-destructive hover:text-destructive"
                                                                data-testid={`delete-layout-${layout.uid}`}
                                                                aria-label={t('common:actions.delete')}
                                                            >
                                                                <AppIcon path={mdiDelete} size="sm" />
                                                            </Button>
                                                        </Can>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <AlertDialog open={pendingDeleteUid !== null} onOpenChange={(open) => { if (!open) setPendingDeleteUid(null) }}>
                <AlertDialogContent data-testid="delete-layout-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('delete_dialog.title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('delete_confirm')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="delete-layout-cancel">{t('delete_dialog.cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDelete} data-testid="delete-layout-confirm">
                            {t('delete_dialog.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <FolderLayoutImportDialog
                org={org}
                open={isImportDialogOpen}
                onOpenChange={setIsImportDialogOpen}
                // A CSV names its target layout by code; offering the existing
                // ones spares the user a trip back to the list.
                layouts={layouts
                    .filter(l => !!l.code)
                    .map(l => ({ code: l.code as string, label: l.name?.en || l.name?.fr || (l.code as string) }))}
                onImported={(result) => {
                    queryClient.invalidateQueries({ queryKey: ['documentLayouts', org] })
                    setImportNotice(t('import.notice_success', {
                        layoutsCreated: result.layouts_created,
                        layoutsUpdated: result.layouts_updated,
                        foldersCreated: result.folders_created,
                        foldersUpdated: result.folders_updated,
                    }))
                }}
            />
        </div>
    )
}
